import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import { getBookingHistory } from '../services/api';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHotel, faCalendarAlt, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import AdPageLayout from '../components/AdPageLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7f9;
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  color: #14464F;
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 700;
  font-size: 30px;
  margin-bottom: 2rem;
`;

const BookingsList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const BookingRow = styled(Link)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  align-items: center;
  padding: 1rem 1.5rem;
  text-decoration: none;
  color: inherit;
  border-bottom: 1px solid #eee;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const BookingInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HotelName = styled.span`
  color: #316C6E;
  font-weight: 600;
`;

const DateInfo = styled.span`
  color: #666;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.status) {
      case 'completed': return '#e8f5e9';
      case 'upcoming': return '#e3f2fd';
      case 'cancelled': return '#ffebee';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#2e7d32';
      case 'upcoming': return '#1565c0';
      case 'cancelled': return '#c62828';
      default: return '#616161';
    }
  }};
`;

const Amount = styled.span`
  font-weight: 500;
`;

const ChevronIcon = styled.span`
  color: #666;
`;

const ListHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
  font-weight: 500;
  color: #666;
  border-bottom: 2px solid #eee;

  @media (max-width: 768px) {
    display: none;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  min-height: 300px;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: #316C6E;
  margin-bottom: 20px;
  opacity: 0.7;
`;

const EmptyStateTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 10px;
`;

const EmptyStateText = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 20px;
  max-width: 400px;
  line-height: 1.5;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

const ShowMoreButton = styled.button`
  background: none;
  border: 2px solid #316C6E;
  color: #316C6E;
  padding: 0.8rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  margin: 2rem auto;
  display: block;
  transition: all 0.3s ease;
  
  &:hover {
    background: #316C6E;
    color: white;
  }
`;

const BookingHistoryPage = () => {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(5);
  const [hasMore, setHasMore] = useState(true);
  const { language } = useLanguage();
  const t = translations[language].pages.bookings;

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const data = await getBookingHistory();
      setBookings(data);
      setHasMore(data.length > 5);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowMore = () => {
    setDisplayCount(prevCount => {
      const newCount = prevCount + 5;
      setHasMore(bookings.length > newCount);
      return newCount;
    });
  };

  const visibleBookings = bookings.slice(0, displayCount);

  return (
    <PageWrapper>
      <Navbar />
      <AdPageLayout>
        <PageContainer>
          <Title>{t.title}</Title>
          {isLoading ? (
            <LoadingWrapper>{t.loading}</LoadingWrapper>
          ) : bookings.length === 0 ? (
            <EmptyStateContainer>
              <EmptyStateIcon>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </EmptyStateIcon>
              <EmptyStateTitle>{t.noBookings}</EmptyStateTitle>
              <EmptyStateText>
                {translations[language].pages.bookings.noBookings}
              </EmptyStateText>
            </EmptyStateContainer>
          ) : (
            <>
              <BookingsList>
                <ListHeader>
                  <div>{translations[language].hotelBooking.roomType}</div>
                  <div>{translations[language].hotelBooking.tripDates}</div>
                  <div>{translations[language].common.status}</div>
                  <div>{translations[language].hotelBooking.totalAmount}</div>
                  <div></div>
                </ListHeader>
                {visibleBookings.map(booking => (
                  <BookingRow key={booking.id} to={`/booking/${booking.id}`}>
                    <BookingInfo>
                      <FontAwesomeIcon icon={faHotel} />
                      <HotelName>{booking.hotelName}</HotelName>
                    </BookingInfo>
                    <DateInfo>
                      {format(new Date(booking.checkInDate), 'MMM d')} - {format(new Date(booking.checkOutDate), 'MMM d, yyyy')}
                    </DateInfo>
                    <StatusBadge status={booking.status || 'completed'}>
                      {booking.status || 'Completed'}
                    </StatusBadge>
                    <Amount>
                      {booking.currency} {booking.totalAmount}
                    </Amount>
                    <ChevronIcon>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </ChevronIcon>
                  </BookingRow>
                ))}
              </BookingsList>
              
              {hasMore && (
                <ShowMoreButton onClick={handleShowMore}>
                  {translations[language].poi.showMore}
                </ShowMoreButton>
              )}
            </>
          )}
        </PageContainer>
      </AdPageLayout>
    </PageWrapper>
  );
};

export default BookingHistoryPage;
